import { useState, useMemo } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { BarChart3, Lock } from 'lucide-react';
import type { UsageData, NotAvailable, DailyUsage, MonthlyUsage, ParticipantDetail } from './overview-types';
import { isNotAvailable } from './overview-types';

interface UsageChartProps {
  usage: UsageData | NotAvailable;
}

type Metric = 'participants' | 'meetings' | 'meetingMinutes';
type TimeRange = '1W' | '1M' | '6M';

const METRIC_CONFIG: Record<Metric, { label: string; color: string }> = {
  participants: { label: 'Participants', color: 'hsl(var(--primary))' },
  meetings: { label: 'Meetings', color: 'hsl(217, 91%, 60%)' },
  meetingMinutes: { label: 'Minutes', color: 'hsl(262, 83%, 58%)' },
};

const TIME_RANGES: { key: TimeRange; label: string }[] = [
  { key: '1W', label: '1W' },
  { key: '1M', label: '1M' },
  { key: '6M', label: '6M' },
];

function isWeekend(dateStr: string): boolean {
  const day = new Date(dateStr + 'T00:00:00').getDay();
  return day === 0 || day === 6;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMonthLabel(monthStr: string): string {
  const [y, m] = monthStr.split('-');
  const d = new Date(Number(y), Number(m) - 1);
  return d.toLocaleDateString('en-US', { month: 'short' });
}

function formatMonthFull(monthStr: string): string {
  const [y, m] = monthStr.split('-');
  const d = new Date(Number(y), Number(m) - 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// --- Tooltip ---

function ParticipantList({ details }: { details?: ParticipantDetail[] }) {
  if (!details || details.length === 0) return null;
  const shown = details.slice(0, 3);
  const remaining = details.length - shown.length;
  return (
    <div className="mt-1.5 border-t border-border/50 pt-1.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-0.5">Active users</p>
      {shown.map((p, i) => (
        <p key={i} className="text-[11px] text-muted-foreground truncate max-w-[200px]">
          {p.name || p.email}
        </p>
      ))}
      {remaining > 0 && <p className="text-[10px] text-muted-foreground/60">+{remaining} more</p>}
    </div>
  );
}

function DailyTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload as ChartDatum | undefined;
  if (!data?.date) return null;

  const dayName = new Date(data.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
  const hasUnique = data.uniqueParticipants != null;

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-foreground">{dayName}, {formatDate(data.date)}</p>
      <div className="mt-1.5 space-y-0.5">
        <TooltipRow color={METRIC_CONFIG.participants.color} label={hasUnique ? 'unique users' : 'participants'} value={data.participants} />
        <TooltipRow color={METRIC_CONFIG.meetings.color} label="meetings" value={data.meetings} />
        <TooltipRow color={METRIC_CONFIG.meetingMinutes.color} label="minutes" value={data.meetingMinutes} />
      </div>
      <ParticipantList details={data.participantDetails} />
    </div>
  );
}

function MonthlyTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload as ChartDatum | undefined;
  if (!data?.month) return null;

  const hasUnique = data.uniqueParticipants != null;

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-foreground">{formatMonthFull(data.month)}</p>
      <div className="mt-1.5 space-y-0.5">
        <TooltipRow color={METRIC_CONFIG.participants.color} label={hasUnique ? 'unique users' : 'participants'} value={data.participants} />
        <TooltipRow color={METRIC_CONFIG.meetings.color} label="meetings" value={data.meetings} />
        <TooltipRow color={METRIC_CONFIG.meetingMinutes.color} label="minutes" value={data.meetingMinutes} />
      </div>
      <ParticipantList details={data.participantDetails} />
    </div>
  );
}

function TooltipRow({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <p className="text-xs text-muted-foreground">
      <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ backgroundColor: color }} />
      {value.toLocaleString()} {label}
    </p>
  );
}

// --- Sample data generators ---

function generateSampleDaily(days: number): DailyUsage[] {
  const now = new Date();
  const data: DailyUsage[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    const dateStr = date.toISOString().split('T')[0]!;
    const isWkend = date.getDay() === 0 || date.getDay() === 6;
    const base = isWkend ? 2 : 8;
    data.push({
      date: dateStr,
      meetings: base + Math.floor(Math.random() * 6),
      participants: (base + Math.floor(Math.random() * 6)) * 3,
      meetingMinutes: (base + Math.floor(Math.random() * 6)) * 45,
      newUsers: Math.random() > 0.7 ? 1 : 0,
    });
  }
  return data;
}

function generateSampleMonthly(): MonthlyUsage[] {
  const now = new Date();
  const data: MonthlyUsage[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const base = 100 + Math.floor(Math.random() * 80);
    data.push({
      month: monthStr,
      meetings: base,
      participants: base * 3,
      meetingMinutes: base * 42,
      newUsers: Math.floor(Math.random() * 5),
    });
  }
  return data;
}

// --- Chart data builders ---

interface ChartDatum {
  label: string;
  weekend: boolean;
  meetings: number;
  participants: number;
  meetingMinutes: number;
  uniqueParticipants?: number;
  participantDetails?: ParticipantDetail[];
  date?: string;
  month?: string;
}

function dailyToChart(data: DailyUsage[], labelFormat: 'day' | 'short' = 'day'): ChartDatum[] {
  return data.map((d) => {
    const dt = new Date(d.date + 'T00:00:00');
    const label = labelFormat === 'short'
      ? dt.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })
      : String(dt.getDate());
    return {
      label,
      weekend: isWeekend(d.date),
      meetings: d.meetings,
      participants: d.uniqueParticipants ?? d.participants,
      meetingMinutes: d.meetingMinutes,
      uniqueParticipants: d.uniqueParticipants,
      participantDetails: d.participantDetails,
      date: d.date,
    };
  });
}

function monthlyToChart(data: MonthlyUsage[]): ChartDatum[] {
  return data.map((d) => ({
    label: formatMonthLabel(d.month),
    weekend: false,
    meetings: d.meetings,
    participants: d.uniqueParticipants ?? d.participants,
    meetingMinutes: d.meetingMinutes,
    uniqueParticipants: d.uniqueParticipants,
    participantDetails: d.participantDetails,
    month: d.month,
  }));
}

// --- Main component ---

export function UsageChart({ usage }: UsageChartProps) {
  const [metric, setMetric] = useState<Metric>('participants');
  const [range, setRange] = useState<TimeRange>('1M');

  const unavailable = isNotAvailable(usage);
  const usageData = unavailable ? null : (usage as UsageData);
  const hasDailyData = (usageData?.dailyBreakdown?.length ?? 0) > 0;

  // Build chart data based on selected range
  const { chartData, tooltipContent, headerLabel } = useMemo(() => {
    if (!hasDailyData) {
      // Sample data for placeholder
      if (range === '6M') {
        const sample = generateSampleMonthly();
        return { chartData: monthlyToChart(sample), tooltipContent: 'monthly' as const, headerLabel: 'Last 6 months (sample)' };
      }
      const days = range === '1W' ? 7 : 30;
      const sample = generateSampleDaily(days);
      return { chartData: dailyToChart(sample), tooltipContent: 'daily' as const, headerLabel: `Last ${days} days (sample)` };
    }

    const allDaily = usageData!.dailyBreakdown; // includes previous + current month
    const monthly = usageData!.monthlyBreakdown;
    const currentMonthPrefix = `${usageData!.year}-${String(usageData!.month).padStart(2, '0')}`;

    if (range === '6M' && monthly?.length > 0) {
      return { chartData: monthlyToChart(monthly), tooltipContent: 'monthly' as const, headerLabel: 'Last 6 months' };
    }

    if (range === '1W') {
      const last7 = allDaily.slice(-7);
      return { chartData: dailyToChart(last7, 'short'), tooltipContent: 'daily' as const, headerLabel: 'Last 7 days' };
    }

    // 1M — show all days of the month, padding future days with zeroes
    const y = usageData!.year;
    const m = usageData!.month;
    const daysInMonth = new Date(y, m, 0).getDate();
    const dailyByDate = new Map(allDaily.filter((d) => d.date.startsWith(currentMonthPrefix)).map((d) => [d.date, d]));
    const paddedMonth: DailyUsage[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentMonthPrefix}-${String(day).padStart(2, '0')}`;
      paddedMonth.push(dailyByDate.get(dateStr) ?? { date: dateStr, meetings: 0, participants: 0, meetingMinutes: 0, newUsers: 0 });
    }
    const monthName = new Date(y, m - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
    return { chartData: dailyToChart(paddedMonth), tooltipContent: 'daily' as const, headerLabel: monthName };
  }, [hasDailyData, usageData, range]);

  const config = METRIC_CONFIG[metric];

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <span className="text-muted-foreground"><BarChart3 className="h-5 w-5" /></span>
        <h3 className="flex-1 text-sm font-semibold text-foreground">Daily Activity</h3>
        <span className="text-xs text-muted-foreground mr-2">{headerLabel}</span>
        {/* Time range toggle */}
        <div className="flex rounded-lg border border-border overflow-hidden">
          {TIME_RANGES.map((tr) => (
            <button
              key={tr.key}
              onClick={() => setRange(tr.key)}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                range === tr.key
                  ? 'bg-primary/15 text-primary'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {tr.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 pb-2">
        {/* Metric toggle */}
        <div className="mb-4 flex gap-1.5">
          {(Object.keys(METRIC_CONFIG) as Metric[]).map((key) => (
            <button
              key={key}
              onClick={() => setMetric(key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                metric === key
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {METRIC_CONFIG[key].label}
            </button>
          ))}
        </div>

        {/* Chart with optional blur overlay */}
        <div className="relative">
          <div className={`h-52 ${!hasDailyData ? 'blur-sm opacity-50 select-none pointer-events-none' : ''}`}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
                barCategoryGap={range === '6M' ? '20%' : '30%'}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  interval={range === '1M' ? 'equidistantPreserveStart' : 0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)}
                />
                <Tooltip
                  content={tooltipContent === 'monthly' ? <MonthlyTooltip /> : <DailyTooltip />}
                  cursor={{ fill: 'hsl(var(--secondary))', opacity: 0.5 }}
                />
                <Bar
                  dataKey={metric}
                  radius={[3, 3, 0, 0]}
                  fill={config.color}
                  fillOpacity={0.8}
                  shape={(props: any) => {
                    const { x, y, width, height, payload } = props;
                    const opacity = payload.weekend ? 0.35 : 0.8;
                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        rx={3}
                        ry={3}
                        fill={config.color}
                        fillOpacity={opacity}
                      />
                    );
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Overlay for unavailable data */}
          {!hasDailyData && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-3 rounded-lg border border-yellow-500/20 bg-card/90 px-5 py-3 shadow-lg backdrop-blur-sm">
                <Lock className="h-4 w-4 shrink-0 text-yellow-500" />
                <div>
                  <p className="text-sm font-medium text-foreground">Usage data unavailable</p>
                  <p className="text-xs text-muted-foreground">
                    Add <code className="rounded bg-secondary px-1 py-0.5 text-[10px]">report:read:admin</code> scope to your Zoom app
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        {hasDailyData && range !== '6M' && (
          <div className="mt-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-4 rounded-sm" style={{ backgroundColor: config.color, opacity: 0.8 }} />
              Weekday
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-4 rounded-sm" style={{ backgroundColor: config.color, opacity: 0.35 }} />
              Weekend
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
